import { Link, Navigate } from "@tanstack/react-router";
import { Lock, Mail, Shield, User } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import {
	type ProtectedRouteOptions,
	type UserRole,
	useProtectedRoute,
} from "../../hooks/use-protected-route";
import { Button } from "../ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { Skeleton } from "../ui/skeleton";

interface ProtectedRouteProps extends ProtectedRouteOptions {
	children: React.ReactNode;
	loadingComponent?: React.ReactNode;
	unauthorizedComponent?: React.ReactNode;
}

const LoadingFallback: React.FC = () => (
	<div className="min-h-screen flex items-center justify-center">
		<div className="space-y-4 w-full max-w-md">
			<Skeleton className="h-8 w-3/4 mx-auto" />
			<Skeleton className="h-4 w-1/2 mx-auto" />
			<Skeleton className="h-32 w-full" />
		</div>
	</div>
);

const ForbiddenFallback: React.FC = () => {
	return <Navigate to="/admin" />;
};

const NotVerifiedFallback: React.FC = () => {
	const { t } = useTranslation();

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<Card className="max-w-md w-full">
				<CardHeader className="text-center">
					<div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
						<Mail className="h-8 w-8 text-primary" />
					</div>
					<CardTitle className="text-accent-foreground">
						{t("auth_guard.verification_required.title")}
					</CardTitle>
					<CardDescription className="text-muted-foreground">
						{t("auth_guard.verification_required.description")}
					</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col items-center gap-3">
					<Button variant="primary" asChild>
						<Link to="/auth/verify-email">
							{t("auth_guard.actions.verify_email")}
						</Link>
					</Button>
					<Button variant="link" asChild>
						<Link to="/">{t("auth_guard.actions.go_home")}</Link>
					</Button>
				</CardContent>
			</Card>
		</div>
	);
};

const UnauthorizedFallback: React.FC<{
	requiredRole?: UserRole | UserRole[];
	currentRole: UserRole;
}> = ({ requiredRole, currentRole }) => {
	const { t } = useTranslation();

	const getIcon = () => {
		if (currentRole === "guest")
			return <User className="h-8 w-8 text-primary" />;
		if (currentRole === "user")
			return <Lock className="h-8 w-8 text-primary" />;
		return <Shield className="h-8 w-8 text-primary" />;
	};

	const getMessage = () => {
		const roles = Array.isArray(requiredRole)
			? requiredRole
			: [requiredRole].filter(Boolean);

		if (roles.includes("admin")) {
			return {
				title: t("auth_guard.admin_required.title"),
				description: t("auth_guard.admin_required.description"),
				action:
					currentRole === "guest" ? (
						<Button asChild>
							<Link to="/auth/signin">{t("auth_guard.actions.sign_in")}</Link>
						</Button>
					) : null,
			};
		}

		if (roles.includes("user")) {
			return {
				title: t("auth_guard.auth_required.title"),
				description: t("auth_guard.auth_required.description"),
				action: (
					<Button asChild>
						<Link to="/auth/signin">{t("auth_guard.actions.sign_in")}</Link>
					</Button>
				),
			};
		}

		return {
			title: t("auth_guard.access_denied.title"),
			description: t("auth_guard.access_denied.description"),
			action: (
				<Button asChild variant="outline">
					<Link to="/">{t("auth_guard.actions.go_home")}</Link>
				</Button>
			),
		};
	};

	const { title, description, action } = getMessage();

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<Card className="max-w-md w-full">
				<CardHeader className="text-center">
					<div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
						{getIcon()}
					</div>
					<CardTitle className="text-accent-foreground">{title}</CardTitle>
					<CardDescription className="text-muted-foreground">
						{description}
					</CardDescription>
				</CardHeader>
				{action && (
					<CardContent className="flex justify-center">{action}</CardContent>
				)}
			</Card>
		</div>
	);
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
	children,
	requiredRole,
	requireVerified,
	redirectTo,
	fallback,
	loadingComponent,
	unauthorizedComponent,
	...options
}) => {
	const { isLoading, hasAccess, userRole, isAuthenticated, isEmailVerified } =
		useProtectedRoute({
			requiredRole,
			requireVerified,
			redirectTo,
			...options,
		});

	// Prevent admins from accessing non-admin routes
	if (!isLoading && userRole === "admin") {
		const requiredRoles = Array.isArray(requiredRole)
			? requiredRole
			: [requiredRole].filter(Boolean);

		// Only allow access to routes that explicitly include "admin" role
		if (!requiredRoles.includes("admin")) {
			return <ForbiddenFallback />;
		}
	}

	if (isLoading) {
		return loadingComponent || <LoadingFallback />;
	}

	if (!hasAccess) {
		if (unauthorizedComponent) {
			return <>{unauthorizedComponent}</>;
		}

		if (fallback) {
			return <>{fallback}</>;
		}

		// Authenticated but email not verified
		if (isAuthenticated && requireVerified && !isEmailVerified) {
			return <NotVerifiedFallback />;
		}

		if (requiredRole === "admin" && isAuthenticated && userRole === "user") {
			return <ForbiddenFallback />;
		}

		const needsUser =
			requiredRole === "user" ||
			(Array.isArray(requiredRole) && requiredRole.includes("user"));
		if (userRole === "guest" && needsUser && !redirectTo) {
			return (
				<UnauthorizedFallback
					requiredRole={requiredRole}
					currentRole={userRole}
				/>
			);
		}

		if (requiredRole === "admin" && userRole === "guest" && !redirectTo) {
			return (
				<UnauthorizedFallback
					requiredRole={requiredRole}
					currentRole={userRole}
				/>
			);
		}

		if (redirectTo) {
			return loadingComponent || <LoadingFallback />;
		}

		return (
			<UnauthorizedFallback
				requiredRole={requiredRole}
				currentRole={userRole}
			/>
		);
	}

	return <>{children}</>;
};

export default ProtectedRoute;
